import React, { useState, useEffect } from "react";

const EMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState(2000000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [tenureType, setTenureType] = useState("years");
  const [prepayment, setPrepayment] = useState(0);
  const [prepaymentFrequency, setPrepaymentFrequency] = useState("yearly");
  const [result, setResult] = useState({
    emi: "0.00",
    totalPayment: "0.00",
    totalInterest: "0.00",
    principalAmount: "0.00",
    interestPercentage: "0",
    principalPercentage: "0",
  });

  // EMI Calculation Function
  const calculateEMI = () => {
    const P = parseFloat(loanAmount);
    const annual_rate = parseFloat(interestRate) / 100;
    const r = annual_rate / 12; // Monthly interest rate
    let tenure = parseFloat(loanTenure);
    const prepay = parseFloat(prepayment);

    if (isNaN(P) || isNaN(r) || isNaN(tenure) || P <= 0 || tenure <= 0) return;

    // Convert tenure to months if in years
    const n = tenureType === "years" ? tenure * 12 : tenure;

    // EMI Formula: EMI = [P × r × (1 + r)^n] / [(1 + r)^n - 1]
    let emi;
    if (r > 0) {
      emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    } else {
      emi = P / n;
    }

    // Calculate with prepayment if any
    let totalInterest = 0;
    let balance = P;
    let monthsPaid = 0;
    const prepayMonthly = prepaymentFrequency === "monthly" ? prepay : 
                         prepaymentFrequency === "yearly" ? prepay / 12 : 0;

    for (let i = 1; i <= n && balance > 0; i++) {
      const interestPayment = balance * r;
      const principalPayment = emi - interestPayment;
      
      balance -= principalPayment;
      totalInterest += interestPayment;
      monthsPaid++;

      // Apply prepayment
      if (prepaymentFrequency === "monthly" && prepayMonthly > 0) {
        balance -= prepayMonthly;
      } else if (prepaymentFrequency === "yearly" && i % 12 === 0 && prepay > 0) {
        balance -= prepay;
      }

      if (balance < 0) balance = 0;
    }

    const totalPayment = (emi * monthsPaid) + (prepaymentFrequency === "monthly" ? prepayMonthly * monthsPaid : 
                        prepaymentFrequency === "yearly" ? prepay * Math.floor(monthsPaid / 12) : 0);
    
    const interestPercentage = ((totalInterest / totalPayment) * 100);
    const principalPercentage = ((P / totalPayment) * 100);

    setResult({
      emi: emi.toFixed(2),
      totalPayment: totalPayment.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      principalAmount: P.toFixed(2),
      interestPercentage: interestPercentage.toFixed(1),
      principalPercentage: principalPercentage.toFixed(1),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
    calculateEMI();
  }, []);

  const tenureLabel = tenureType === "years" ? "Years" : "Months";

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>EMI Calculator</h1>
          <p>
            Calculate your Equated Monthly Installment for home, car, or personal loans
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Loan Details
              </h2>

              {/* Loan Amount */}
              <div className="input-group">
                <label>Loan Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(e.target.value)}
                    className="calc-input"
                    placeholder="2000000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setLoanAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 100000).toFixed(0)}L
                    </button>
                  ))}
                </div>
              </div>

              {/* Interest Rate */}
              <div className="input-group">
                <label>Interest Rate (Annual)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="8.5"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[7, 8.5, 10, 12].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Loan Tenure */}
              <div className="input-group">
                <label>Loan Tenure</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={loanTenure}
                    onChange={(e) => setLoanTenure(e.target.value)}
                    className="calc-input"
                    placeholder="20"
                  />
                </div>
                <div className="quick-select-group">
                  {tenureType === "years" 
                    ? [5, 10, 15, 20, 30].map((years) => (
                        <button
                          key={years}
                          onClick={() => setLoanTenure(years)}
                          className="quick-select-btn success"
                        >
                          {years}Y
                        </button>
                      ))
                    : [12, 24, 36, 60].map((months) => (
                        <button
                          key={months}
                          onClick={() => setLoanTenure(months)}
                          className="quick-select-btn success"
                        >
                          {months}M
                        </button>
                      ))
                  }
                </div>
              </div>

              {/* Tenure Type */}
              <div className="input-group">
                <label>Tenure Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "years", label: "Years" },
                    { value: "months", label: "Months" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setTenureType(type.value);
                        setLoanTenure(type.value === "years" ? 20 : 240);
                      }}
                      className={`quick-select-btn ${tenureType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prepayment Amount */}
              <div className="input-group">
                <label>Prepayment Amount (Optional)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={prepayment}
                    onChange={(e) => setPrepayment(e.target.value)}
                    className="calc-input"
                    placeholder="0"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 10000, 25000, 50000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setPrepayment(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prepayment Frequency */}
              {prepayment > 0 && (
                <div className="input-group">
                  <label>Prepayment Frequency</label>
                  <div className="quick-select-group">
                    {[
                      { value: "monthly", label: "Monthly" },
                      { value: "yearly", label: "Yearly" }
                    ].map((freq) => (
                      <button
                        key={freq.value}
                        onClick={() => setPrepaymentFrequency(freq.value)}
                        className={`quick-select-btn ${prepaymentFrequency === freq.value ? 'primary' : 'secondary'}`}
                      >
                        {freq.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={calculateEMI} className="calculate-btn">
                Calculate EMI
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                EMI Summary
              </h2>

              <div className="result-card">
                {/* Monthly EMI - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Monthly EMI</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.emi).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Payable for {loanTenure} {tenureLabel.toLowerCase()}
                  </div>
                </div>

                {/* Principal Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Principal Amount</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.principalAmount).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Original loan amount
                  </div>
                </div>

                {/* Total Interest */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Interest</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInterest).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Interest paid over tenure
                  </div>
                </div>

                {/* Total Payment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Amount Payable</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalPayment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Principal + Interest {prepayment > 0 ? "+ Prepayment" : ""}
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Payment Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Principal Amount</span>
                      <span className="breakdown-percentage">
                        {result.principalPercentage}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${result.principalPercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total Interest</span>
                      <span className="breakdown-percentage">
                        {result.interestPercentage}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${result.interestPercentage}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the EMI Calculator Work?</h3>
              <p>
                The EMI Calculator uses the standard loan amortization formula to calculate your 
                monthly installment. EMI (Equated Monthly Installment) is a fixed payment amount 
                made by a borrower to a lender at a specified date each month. It comprises both 
                principal and interest components. In the early years, a larger portion goes toward 
                interest, while later payments have more principal component. This calculator also 
                factors in prepayments to show how extra payments can reduce your loan tenure and 
                interest burden.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>EMI Formula Used:</h4>
            <div className="formula-display">
              EMI = [P × r × (1 + r)^n] / [(1 + r)^n - 1]
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">EMI</div>
                <div className="formula-param-value">Equated Monthly Installment</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Principal Loan Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Monthly Interest Rate (Annual/12)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Loan Tenure in Months</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is EMI and how is it calculated?</h5>
              <p>
                EMI (Equated Monthly Installment) is a fixed monthly payment you make to repay a 
                loan. It includes both principal and interest components. The calculation considers 
                your loan amount, interest rate, and tenure. In the initial months, interest forms 
                a larger portion of EMI, while in later stages, principal repayment dominates. This 
                is called loan amortization. Banks use the reducing balance method where interest 
                is calculated on the outstanding principal, not the original amount.
              </p>
            </div>

            <div className="faq-item">
              <h5>How can I reduce my EMI burden?</h5>
              <p>
                You can reduce EMI by increasing loan tenure (but this increases total interest), 
                making a larger down payment to reduce principal, negotiating for lower interest 
                rates, making prepayments whenever possible, or refinancing at lower rates. For 
                home loans, you can also claim tax benefits under Section 80C (principal) and 
                Section 24(b) (interest) which effectively reduces your cost. Balance transfer to 
                banks offering lower rates is another option after 1-2 years of loan.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I prepay my loan or invest the extra money?</h5>
              <p>
                Compare your loan interest rate with potential investment returns. If your loan 
                interest is 8% and you can earn 12% post-tax from investments, investing makes 
                sense. However, prepaying loans provides guaranteed returns (saved interest) and 
                reduces financial liability. Generally, prepay high-interest loans (personal loans, 
                credit cards). For low-interest home loans with tax benefits, investing might be 
                better. Consider your risk appetite, financial goals, and peace of mind factor.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the difference between fixed and floating interest rates?</h5>
              <p>
                Fixed rates remain constant throughout the loan tenure, giving EMI predictability 
                but are typically 1-2% higher. Floating rates vary with market conditions (repo 
                rate changes) - they can decrease or increase, affecting your EMI or tenure. Most 
                home loans in India are floating. If you expect rates to fall, choose floating. 
                If rates are at historic lows, fixed might be better. Some banks offer partially 
                fixed loans (fixed for initial years, then floating).
              </p>
            </div>

            <div className="faq-item">
              <h5>How much loan can I afford based on my income?</h5>
              <p>
                Financial experts recommend keeping your EMI below 40-50% of your monthly income 
                (including all loans). This is called the FOIR (Fixed Obligation to Income Ratio). 
                Banks typically offer loans where EMI doesn't exceed 40-50% of your income. For 
                example, with ₹1 lakh monthly income, your total EMIs shouldn't exceed ₹40,000-50,000. 
                Consider other expenses like rent, children's education, insurance, and maintain an 
                emergency fund. Don't max out your borrowing capacity just because banks approve it.
              </p>
            </div>

            <div className="faq-item">
              <h5>What happens if I miss an EMI payment?</h5>
              <p>
                Missing EMI payments has serious consequences: late payment charges (2-3% of EMI), 
                penal interest on outstanding amount, negative impact on credit score (CIBIL drops 
                significantly), difficulty getting future loans, bank may issue legal notice after 
                3+ missed payments, and in extreme cases, asset seizure for secured loans. If facing 
                financial difficulty, contact your bank immediately. They may offer restructuring, 
                EMI moratorium, or tenure extension. Never ignore missed payments.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EMICalculator;